import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, MapPin, Users, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { OnboardingProgress } from "@/components/Onboarding/OnboardingProgress";
import AuthLeftPanel from "@/components/Auth/AuthLeftPanel";
// Validador CNPJ
function isValidCNPJ(cnpj: string): boolean {
  const cleanCNPJ = cnpj.replace(/\D/g, "");
  if (cleanCNPJ.length !== 14) return false;
  if (/^(\d)\1+$/.test(cleanCNPJ)) return false;

  let size = cleanCNPJ.length - 2;
  let numbers = cleanCNPJ.substring(0, size);
  const digits = cleanCNPJ.substring(size);
  let sum = 0;
  let pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += parseInt(numbers.charAt(size - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) return false;

  size = size + 1;
  numbers = cleanCNPJ.substring(0, size);
  sum = 0;
  pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += parseInt(numbers.charAt(size - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  return result === parseInt(digits.charAt(1));
}

function formatCNPJ(value: string): string {
  const cleaned = value.replace(/\D/g, "").slice(0, 14);
  if (cleaned.length <= 2) return cleaned;
  if (cleaned.length <= 5) return `${cleaned.slice(0, 2)}.${cleaned.slice(2)}`;
  if (cleaned.length <= 8) return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5)}`;
  if (cleaned.length <= 12) return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8)}`;
  return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8, 12)}-${cleaned.slice(12)}`;
}

function formatCEP(value: string): string {
  const cleaned = value.replace(/\D/g, "").slice(0, 8);
  if (cleaned.length <= 5) return cleaned;
  return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
}

const companySchema = z.object({
  razaoSocial: z.string().min(3, "Razão social é obrigatória"),
  nomeFantasia: z.string().optional(),
  cnpj: z
    .string()
    .min(18, "CNPJ inválido")
    .refine((val) => isValidCNPJ(val), "CNPJ inválido"),
  cep: z.string().min(9, "CEP inválido"),
  endereco: z.string().min(3, "Endereço é obrigatório"),
  cidade: z.string().min(2, "Cidade é obrigatória"),
  estado: z.string().min(2, "Estado é obrigatório"),
  quantidadeColaboradores: z.string().min(1, "Selecione a quantidade"),
  areaAtuacao: z.string().min(1, "Selecione a área"),
});

type CompanyFormData = z.infer<typeof companySchema>;

const employeeRanges = [
  "1-10",
  "11-50",
  "51-200",
  "201-500",
  "501-1000",
  "1000+",
];

const businessAreas = [
  "Tecnologia",
  "Saúde",
  "Educação",
  "Financeiro",
  "Indústria",
  "Varejo",
  "Serviços",
  "Governo",
  "Outro",
];

export default function CompanyData() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCEP, setIsLoadingCEP] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
  });

  // Verificar autenticação
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        navigate("/auth");
        return;
      }

      if (!session.user.email_confirmed_at) {
        navigate("/verify-email");
        return;
      }

      // Verificar etapa do onboarding
      const { data: profile } = await supabase
        .from("profiles")
        .select("onboarding_step, client_id")
        .eq("id", session.user.id)
        .single();

      if (profile) {
        // Se ainda não completou dados pessoais
        if (profile.onboarding_step === "pending" || 
            profile.onboarding_step === "email_sent" ||
            profile.onboarding_step === "verified") {
          navigate("/onboarding/personal");
          return;
        }

        // Se já completou empresa
        if (profile.onboarding_step === "plan" || 
            profile.onboarding_step === "completed" ||
            profile.client_id) {
          navigate("/onboarding/plan");
          return;
        }
      }

      setIsCheckingAuth(false);
    };

    checkAuth();
  }, [navigate]);

  const handleCNPJChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("cnpj", formatCNPJ(e.target.value));
  };

  const handleCEPChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedCEP = formatCEP(e.target.value);
    setValue("cep", formattedCEP);

    // Auto-complete via ViaCEP
    const cleanCEP = formattedCEP.replace(/\D/g, "");
    if (cleanCEP.length === 8) {
      setIsLoadingCEP(true);
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cleanCEP}/json/`);
        const data = await response.json();
        
        if (!data.erro) {
          setValue("endereco", `${data.logradouro}, ${data.bairro}`);
          setValue("cidade", data.localidade);
          setValue("estado", data.uf);
        }
      } catch (error) {
        console.error("Erro ao buscar CEP:", error);
      } finally {
        setIsLoadingCEP(false);
      }
    }
  };

  const onSubmit = async (data: CompanyFormData) => {
    setIsLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        navigate("/auth");
        return;
      }

      const cleanCNPJ = data.cnpj.replace(/\D/g, "");

      // Verificar se CNPJ já existe
      const { data: existingClient } = await supabase
        .from("clients")
        .select("id")
        .eq("cnpj", cleanCNPJ)
        .maybeSingle();

      if (existingClient) {
        toast({
          title: "CNPJ já cadastrado",
          description: "Esta empresa já está cadastrada no sistema.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      // Buscar dados do usuário para preencher responsável
      const { data: profileData } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", session.user.id)
        .single();

      // Criar cliente
      const { data: newClient, error: clientError } = await supabase
        .from("clients")
        .insert({
          name: data.razaoSocial,
          razao_social: data.razaoSocial,
          nome_fantasia: data.nomeFantasia || null,
          cnpj: cleanCNPJ,
          cep: data.cep.replace(/\D/g, ""),
          address: data.endereco,
          cidade: data.cidade,
          estado: data.estado,
          email: session.user.email!,
          phone: "",
          quantidade_colaboradores: data.quantidadeColaboradores,
          area_atuacao: data.areaAtuacao,
          responsavel_nome: profileData?.full_name || null,
          responsavel_cpf: null,
          responsavel_telefone: null,
          responsavel_cargo: null,
          status: "active",
          created_by: session.user.id,
        })
        .select()
        .single();

      if (clientError) throw clientError;

      // Atualizar profile com client_id
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          client_id: newClient.id,
          onboarding_step: "company",
        })
        .eq("id", session.user.id);

      if (profileError) throw profileError;

      // O trigger sync_profile_to_users sincroniza automaticamente o client_id para users

      // Log de auditoria
      await supabase.from("audit_logs").insert({
        user_id: session.user.id,
        event_type: "onboarding_company_completed",
        metadata: { 
          step: "company",
          client_id: newClient.id,
        },
      });

      toast({
        title: "Empresa cadastrada",
        description: "Dados da empresa salvos com sucesso.",
      });

      navigate("/onboarding/plan");

    } catch (error: any) {
      console.error("[CompanyData] Erro:", error);
      toast({
        title: "Erro ao salvar",
        description: error.message || "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen w-full flex flex-col lg:flex-row">
        <AuthLeftPanel />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row">
      <AuthLeftPanel />
      <div 
        className="flex-1 flex items-center justify-center bg-gradient-to-tl from-amber-50 via-orange-50 to-white min-h-screen lg:min-h-0 p-4 sm:p-6 lg:p-8 overflow-y-auto"
        style={{
          backgroundImage: 'url(/logos/images/fundo_apresentacao_eleeva.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <Card className="w-full max-w-lg border-2 border-amber-200 bg-white/95 backdrop-blur-sm shadow-2xl shadow-amber-200/20">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">Dados da Empresa</CardTitle>
            <CardDescription>
              Informações da sua organização
            </CardDescription>
            <div className="mt-4">
              <OnboardingProgress currentStep={2} totalSteps={4} />
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Razão Social */}
              <div className="space-y-2">
                <Label htmlFor="razaoSocial">Razão Social *</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="razaoSocial"
                    placeholder="Razão social da empresa"
                    className="pl-10"
                    {...register("razaoSocial")}
                  />
                </div>
                {errors.razaoSocial && (
                  <p className="text-sm text-destructive">{errors.razaoSocial.message}</p>
                )}
              </div>

              {/* Nome Fantasia */}
              <div className="space-y-2">
                <Label htmlFor="nomeFantasia">Nome Fantasia</Label>
                <Input
                  id="nomeFantasia"
                  placeholder="Nome fantasia (opcional)"
                  {...register("nomeFantasia")}
                />
              </div>

              {/* CNPJ */}
              <div className="space-y-2">
                <Label htmlFor="cnpj">CNPJ *</Label>
                <Input
                  id="cnpj"
                  placeholder="00.000.000/0000-00"
                  {...register("cnpj")}
                  onChange={handleCNPJChange}
                />
                {errors.cnpj && (
                  <p className="text-sm text-destructive">{errors.cnpj.message}</p>
                )}
              </div>

              {/* CEP e Endereço */}
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="cep">CEP *</Label>
                  <div className="relative">
                    <Input
                      id="cep"
                      placeholder="00000-000"
                      {...register("cep")}
                      onChange={handleCEPChange}
                    />
                    {isLoadingCEP && (
                      <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                  </div>
                  {errors.cep && (
                    <p className="text-sm text-destructive">{errors.cep.message}</p>
                  )}
                </div>

                <div className="col-span-2 space-y-2">
                  <Label htmlFor="endereco">Endereço *</Label>
                  <Input
                    id="endereco"
                    placeholder="Endereço completo"
                    {...register("endereco")}
                  />
                  {errors.endereco && (
                    <p className="text-sm text-destructive">{errors.endereco.message}</p>
                  )}
                </div>
              </div>

              {/* Cidade e Estado */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="cidade">Cidade *</Label>
                  <Input
                    id="cidade"
                    placeholder="Cidade"
                    {...register("cidade")}
                  />
                  {errors.cidade && (
                    <p className="text-sm text-destructive">{errors.cidade.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="estado">Estado *</Label>
                  <Input
                    id="estado"
                    placeholder="UF"
                    maxLength={2}
                    {...register("estado")}
                  />
                  {errors.estado && (
                    <p className="text-sm text-destructive">{errors.estado.message}</p>
                  )}
                </div>
              </div>

              {/* Quantidade de Colaboradores */}
              <div className="space-y-2">
                <Label>Quantidade de Colaboradores *</Label>
                <Select onValueChange={(value) => setValue("quantidadeColaboradores", value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {employeeRanges.map((range) => (
                      <SelectItem key={range} value={range}>
                        {range} colaboradores
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.quantidadeColaboradores && (
                  <p className="text-sm text-destructive">{errors.quantidadeColaboradores.message}</p>
                )}
              </div>

              {/* Área de Atuação */}
              <div className="space-y-2">
                <Label>Área de Atuação *</Label>
                <Select onValueChange={(value) => setValue("areaAtuacao", value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {businessAreas.map((area) => (
                      <SelectItem key={area} value={area}>
                        {area}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.areaAtuacao && (
                  <p className="text-sm text-destructive">{errors.areaAtuacao.message}</p>
                )}
              </div>

              {/* Botões */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/onboarding/personal")}
                  className="gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Voltar
                </Button>
                <Button type="submit" className="flex-1 gap-2" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      Continuar
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
